/**
 * 토스트 알림 시스템 Hook
 *
 * 사용자에게 임시 피드백 메시지를 표시하는 Zustand Store 기반 Hook입니다.
 * 성공, 에러, 정보, 경고, 로딩 등 5가지 유형의 메시지를 지원합니다.
 *
 * 각 타입별 기본 자동 닫기 시간:
 * - success: 3초
 * - error: 5초 (사용자가 읽을 시간 충분히 제공)
 * - info: 3초
 * - warning: 4초
 * - loading: 0초 (수동으로 닫아야 함)
 * - 기본 toast: 4초
 *
 * @hook
 * @example
 * // 기본 사용 (성공 메시지)
 * const { success, error } = useToast();
 *
 * const handleSubmit = async (data) => {
 *   try {
 *     await saveData(data);
 *     success('저장되었습니다!');
 *   } catch (err) {
 *     error('저장에 실패했습니다.');
 *   }
 * };
 *
 * @example
 * // 로딩 메시지와 수동 닫기
 * const { loading, success } = useToast();
 *
 * const handleLongOperation = async () => {
 *   const toastId = loading('처리 중입니다...');
 *   try {
 *     await longOperation();
 *     success('완료되었습니다!');
 *   } catch (err) {
 *     error('작업 실패');
 *   }
 * };
 *
 * @example
 * // 커스텀 옵션 (자동 닫기 시간 조정)
 * const { success } = useToast();
 * success('잠깐, 이것을 읽어보세요!', { duration: 10000 }); // 10초
 *
 * @returns {UseToastReturn} 토스트 관리 메서드
 */

'use client';

import { useToastStore, type AppToast } from '@/lib/stores/toastStore';
import { generateRandomId } from '@/lib/utils/idUtils';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

// ============================================================================
// 타입별 기본 duration 설정
// ============================================================================

const DEFAULT_DURATIONS: Record<ToastType | 'loading', number> = {
  success: 3000,
  error: 5000,
  info: 3000,
  warning: 4000,
  loading: 0, // 수동으로 닫아야 함
};

export interface UseToastReturn {
  /**
   * 특정 타입의 toast 표시
   */
  toast: (type: ToastType, message: string, options?: Partial<AppToast>) => void;

  /**
   * 성공 메시지
   */
  success: (message: string, options?: Partial<AppToast>) => void;

  /**
   * 에러 메시지
   */
  error: (message: string, options?: Partial<AppToast>) => void;

  /**
   * 정보 메시지
   */
  info: (message: string, options?: Partial<AppToast>) => void;

  /**
   * 경고 메시지
   */
  warning: (message: string, options?: Partial<AppToast>) => void;

  /**
   * 로딩 메시지
   */
  loading: (message: string, options?: Partial<AppToast>) => void;

  /**
   * 모든 toast 제거
   */
  clear: () => void;
}

export function useToast(): UseToastReturn {
  const { addToast, clearToasts } = useToastStore();

  // 팩토리 함수: 특정 타입의 토스트 핸들러 생성
  const createToastHandler =
    (type: ToastType, duration: number) =>
    (message: string, options?: Partial<AppToast>) => {
      addToast({
        id: generateRandomId(),
        type,
        message,
        duration,
        ...options,
      });
    };

  return {
    toast: (type: ToastType, message: string, options?: Partial<AppToast>) => {
      addToast({
        id: generateRandomId(),
        type,
        message,
        duration: DEFAULT_DURATIONS[type] ?? 4000,
        ...options,
      });
    },

    success: createToastHandler('success', DEFAULT_DURATIONS.success),
    error: createToastHandler('error', DEFAULT_DURATIONS.error),
    info: createToastHandler('info', DEFAULT_DURATIONS.info),
    warning: createToastHandler('warning', DEFAULT_DURATIONS.warning),

    loading: (message: string, options?: Partial<AppToast>) => {
      addToast({
        id: generateRandomId(),
        type: 'info',
        message,
        duration: DEFAULT_DURATIONS.loading, // 0: 수동으로 닫아야 함
        ...options,
      });
    },

    clear: clearToasts,
  };
}
